import {Scale, Activity, CheckCircle2, XCircle} from 'lucide-react'

const comparisons = [
  {
    metric: "BMI",
    icon: Scale,
    color: "amber",
    purpose: "Classifies your weight category (underweight, normal, overweight, obese) based on height and weight alone.",
    strengths: [
      "Quick snapshot of general health risk",
      "Easy to calculate — no activity data needed",
      "Widely recognized by healthcare providers",
    ],
    limitations: [
      "Ignores muscle mass, bone density & body composition",
      "Does not reflect how many calories you actually burn",
      "Cannot determine precise macronutrient needs",
    ],
  },
  {
    metric: "TDEE",
    icon: Activity,
    color: "emerald",
    purpose: "Measures the total calories your body burns daily, accounting for metabolism, activity level, and fitness goals.",
    strengths: [
      "Personalized to your exact activity level",
      "Directly converts to fat grams & macros",
      "Adjusts dynamically for weight loss or muscle gain",
    ],
    limitations: [
      "Requires accurate activity-level input",
      "Estimates can vary with metabolic conditions",
      "Needs recalculation as weight or habits change",
    ],
  },
]

export default function TDEEvsBMISection() {
  return (
    <section id="tdee-vs-bmi" className="relative py-16 sm:py-20 bg-gray-50 noise-bg">
      <div className="max-w-6xl mx-auto px-4 sm:px-6">
        {/* Header */}
        <div className="max-w-3xl mb-10">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-slate-100 border border-slate-200 mb-4">
            <Scale className="w-4 h-4 text-slate-600" />
            <span className="text-xs font-semibold text-slate-600 uppercase tracking-wide">
              TDEE vs BMI
            </span>
          </div>
          <h2
            className="text-2xl sm:text-3xl lg:text-4xl font-bold text-slate-800 mb-4"
            style={{ fontFamily: "'DM Sans', sans-serif" }}
          >
            Why TDEE is More Important Than BMI for Daily Fat Intake
          </h2>
          <p className="text-base sm:text-lg text-slate-600 leading-relaxed">
            Both BMI and TDEE play a role in understanding your health — but when it comes to
            determining <strong>exactly how many grams of fat you should eat</strong>, they serve very
            different purposes. Here is why TDEE is the metric that matters most for your daily
            macronutrient planning.
          </p>
        </div>

        {/* Comparison Cards */}
        <div className="grid md:grid-cols-2 gap-6 mb-12">
          {comparisons.map((item) => {
            const Icon = item.icon
            const isEmerald = item.color === "emerald"
            return (
              <div
                key={item.metric}
                className={`bg-white rounded-2xl border shadow-sm overflow-hidden transition-all hover:shadow-lg hover:-translate-y-0.5 duration-300 ${
                  isEmerald ? "border-emerald-200" : "border-amber-200"
                }`}
              >
                {/* Card header */}
                <div
                  className={`px-6 py-4 flex items-center gap-3 border-b ${
                    isEmerald
                      ? "bg-emerald-50 border-emerald-100"
                      : "bg-amber-50 border-amber-100"
                  }`}
                >
                  <div
                    className={`w-10 h-10 rounded-xl flex items-center justify-center ${
                      isEmerald ? "bg-emerald-500/10" : "bg-amber-500/10"
                    }`}
                  >
                    <Icon
                      className={`w-5 h-5 ${
                        isEmerald ? "text-emerald-600" : "text-amber-600"
                      }`}
                    />
                  </div>
                  <div>
                    <h3
                      className="text-lg font-bold text-slate-800"
                      style={{ fontFamily: "'DM Sans', sans-serif" }}
                    >
                      {item.metric}
                    </h3>
                    <p className="text-xs text-slate-500">
                      {item.metric === "BMI"
                        ? "Body Mass Index"
                        : "Total Daily Energy Expenditure"}
                    </p>
                  </div>
                </div>

                {/* Card body */}
                <div className="p-6">
                  <p className="text-sm text-slate-600 leading-relaxed mb-5">
                    {item.purpose}
                  </p>

                  {/* Strengths */}
                  <div className="mb-4">
                    <p className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-2">
                      Strengths
                    </p>
                    <ul className="space-y-2">
                      {item.strengths.map((s) => (
                        <li key={s} className="flex items-start gap-2 text-sm text-slate-700">
                          <CheckCircle2 className="w-4 h-4 text-emerald-500 flex-shrink-0 mt-0.5" />
                          <span>{s}</span>
                        </li>
                      ))}
                    </ul>
                  </div>

                  {/* Limitations */}
                  <div>
                    <p className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-2">
                      Limitations
                    </p>
                    <ul className="space-y-2">
                      {item.limitations.map((l) => (
                        <li key={l} className="flex items-start gap-2 text-sm text-slate-600">
                          <XCircle className="w-4 h-4 text-red-400 flex-shrink-0 mt-0.5" />
                          <span>{l}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            )
          })}
        </div>

        {/* Bottom summary */}
        <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-6 sm:p-8">
          <h3
            className="text-xl font-bold text-slate-800 mb-4"
            style={{ fontFamily: "'DM Sans', sans-serif" }}
          >
            The Bottom Line
          </h3>
          <div className="space-y-4">
            <p className="text-base text-slate-600 leading-relaxed">
              Think of <strong>BMI as a health category label</strong> and <strong>TDEE as your actual
              fuel gauge</strong>. BMI tells you whether your weight falls within a generally healthy
              range — useful for screening, but it cannot tell you how many grams of fat to put on
              your plate. A muscular athlete and a sedentary office worker can share the same BMI, yet
              their daily fat needs may differ by 40+ grams.
            </p>
            <p className="text-base text-slate-600 leading-relaxed">
              TDEE, on the other hand, factors in <strong>everything that affects calorie burn</strong>
              — your basal metabolism, daily movement, exercise intensity, and even your specific
              fitness goal (weight loss, maintenance, or muscle gain). When you calculate 20–35 % of
              TDEE as fat, you get a precise gram range tailored to your body. That is why every
              serious nutrition plan — from clinical dietetics to competitive bodybuilding — starts
              with TDEE, not BMI.
            </p>
            <p className="text-sm text-slate-500 leading-relaxed">
              <strong>Our recommendation:</strong> Use BMI as a quick health check, but rely on your
              TDEE-based fat calculation for meal planning and macro tracking. The calculator above
              does both for you automatically.
            </p>
          </div>
        </div>
      </div>
    </section>
  )
}
